import { Flex, Text } from '@mantine/core';
import { useMemo } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import SelectableItemList from './SelectableItemList';
import { StepProps } from './DrawerFlow';
import DrawerFlowScreenLayout from './DrawerFlowScreenLayout';
import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import pluralize from 'pluralize';

type DataWithTemplate = {
  selectedTemplateId?: string;
};

type SelectTemplateStepProps<T extends DataWithTemplate> = StepProps<T> & {
  type: 'SUITABILITY_REPORT' | 'MEETING_AGENDA' | 'MEETING_NOTE';
  description: string;
  clientNameSuffix?: string;
  continueText?: string;
  isSubmitting?: boolean;
};

export default function SelectTemplateStep<T extends DataWithTemplate>({
  type,
  description,
  clientNameSuffix = 'report',
  continueText,
  isSubmitting,
  ...props
}: SelectTemplateStepProps<T>) {
  const { files, isLoading } = useTemplates({
    templateType: type,
  });
  const { data, setData } = props;

  const items = useMemo(
    () =>
      files.map(template => {
        const title = template.displayName?.trim()
          ? template.displayName
          : template.file?.fileName || 'Undefined';
        return {
          id: template.id,
          title,
        };
      }),
    [files],
  );

  const handleTemplateSelection = (templateIds: string[]) => {
    setData({
      ...data,
      selectedTemplateId: templateIds[0],
    });
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      canGoForward={props.canGoForward && !!data.selectedTemplateId}
      isLoading={isLoading}
      title="Select template"
      description={description}
      clientNameSuffix={clientNameSuffix}
      continueText={continueText}
      isSubmitting={isSubmitting}
    >
      <Flex direction="column" gap="xs">
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black">
          {files.length} {pluralize('template', files.length)} found
        </Text>
        <SelectableItemList
          items={items}
          selectedItemIds={data.selectedTemplateId ? [data.selectedTemplateId] : []}
          setSelectedItemIds={handleTemplateSelection}
          selectMultiple={false}
        />
      </Flex>
    </DrawerFlowScreenLayout>
  );
}
